import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { GrowthNavSection } from '../data/growthViewModel';
import { Icon } from './shared/Icon';

/**
 * Growth in the left sidebar with its sections as sub-items (they replace
 * Growth's in-page tab strip). Expanded, the sections show indented under
 * Growth while Growth is open. Collapsed (the icon rail, and on phones),
 * the Growth icon opens a small menu of the sections: arrow keys move,
 * Escape closes it and focus returns to the icon. The open section is
 * marked with `aria-current="page"`.
 */
export function GrowthNavGroup({
  expanded,
  active,
  sections,
  current,
  attention = false,
  onSelect,
  tip,
}: {
  expanded: boolean;
  /** Growth is the screen on display. */
  active: boolean;
  /** The sections listed (Website and Local and search only when connected). */
  sections: readonly GrowthNavSection[];
  /** The section on display, while Growth is. */
  current: string | null;
  /** Data health has something that needs action: a quiet dot on its item. */
  attention?: boolean;
  /** Opens Growth on a section (null: Growth as it was). */
  onSelect: (section: GrowthNavSection | null) => void;
  /** Collapsed rail: shows / hides the "Growth" tip beside the icon. */
  tip?: { show: (el: HTMLElement) => void; hide: () => void };
}) {
  const { t } = useTranslation('nav');
  const { t: tg } = useTranslation('growth');
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();
  const currentSection = active && current && (sections as readonly string[]).includes(current) ? current : null;

  const label = (id: GrowthNavSection) => tg(`tabs.${id}`);
  const itemLabel = (id: GrowthNavSection) =>
    id === 'health' && attention ? `${label(id)}, ${t('growthAttention')}` : label(id);

  // The expanded rail has no menu.
  useEffect(() => {
    if (expanded) setOpen(false);
  }, [expanded]);

  // Opened: focus the open section (or the first); a click outside closes.
  useEffect(() => {
    if (!open) return undefined;
    const menu = menuRef.current;
    const items = menu ? [...menu.querySelectorAll<HTMLButtonElement>('[role="menuitem"]')] : [];
    (items.find(item => item.getAttribute('aria-current') === 'page') ?? items[0])?.focus();
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) buttonRef.current?.focus();
  };

  const onMenuKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const items = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]')];
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === 'Tab') {
      close(false);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      items[(at + step + items.length) % items.length]?.focus();
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      items[event.key === 'Home' ? 0 : items.length - 1]?.focus();
    }
  };

  const dot = attention ? <span className="nav-rail-dot" aria-hidden="true" /> : null;

  if (expanded) {
    return (
      <div className={`nav-rail-group${active ? ' is-open' : ''}`}>
        <button
          type="button"
          className={`nav-rail-item${active ? ' on' : ''}`}
          onClick={() => onSelect(null)}
          aria-current={active && !currentSection ? 'page' : undefined}
          aria-expanded={active}
        >
          <Icon name="web" size={15} />
          <span className="nav-rail-label">{t('growth')}</span>
        </button>
        {active ? (
          <ul className="nav-rail-sub" aria-label={t('growthSections')}>
            {sections.map(id => (
              <li key={id}>
                <button
                  type="button"
                  className={`nav-rail-subitem${currentSection === id ? ' on' : ''}`}
                  data-growth-section={id}
                  onClick={() => onSelect(id)}
                  aria-current={currentSection === id ? 'page' : undefined}
                  aria-label={itemLabel(id)}
                >
                  <span className="nav-rail-label">{label(id)}</span>
                  {id === 'health' ? dot : null}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }

  return (
    <div className="nav-rail-group is-collapsed">
      <button
        ref={buttonRef}
        type="button"
        className={`nav-rail-item${active ? ' on' : ''}`}
        onClick={() => {
          tip?.hide();
          setOpen(value => !value);
        }}
        onMouseEnter={event => {
          if (!open) tip?.show(event.currentTarget);
        }}
        onMouseLeave={tip?.hide}
        onFocus={event => {
          if (!open) tip?.show(event.currentTarget);
        }}
        onBlur={tip?.hide}
        onKeyDown={event => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-current={active ? 'page' : undefined}
        aria-label={attention ? `${t('growth')}, ${t('growthAttention')}` : t('growth')}
      >
        <Icon name="web" size={15} />
        {attention ? <span className="nav-rail-dot is-badge" aria-hidden="true" /> : null}
      </button>
      {open ? (
        <div
          ref={menuRef}
          id={menuId}
          className="nav-rail-menu"
          role="menu"
          aria-label={t('growthSections')}
          onKeyDown={onMenuKey}
        >
          <p className="nav-rail-menu-title" aria-hidden="true">
            {t('growth')}
          </p>
          {sections.map(id => (
            <button
              key={id}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={`nav-rail-menu-item${currentSection === id ? ' on' : ''}`}
              data-growth-section={id}
              aria-current={currentSection === id ? 'page' : undefined}
              aria-label={itemLabel(id)}
              onClick={() => {
                onSelect(id);
                close();
              }}
            >
              <span className="nav-rail-label">{label(id)}</span>
              {id === 'health' ? dot : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
